// @ts-nocheck
import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
// import { css } from "styled-components"; // Store Custom Code

/* Starter Custom Code - Start */
import { headerHideForMobileStyles } from "@store/Themes/common/mixins/header";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
/* Starter Custom Code - End */

/* Store Custom Code - Start */
import usePublishHeightVariable from "@store/Framework/Common/Hooks/usePublishHeightVariable";
import { stickyHeaderHeightVariable } from "@store/Framework/Common/Utilities/getStickyHeaderHeight";
import { css } from "styled-components";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";

export const enum headerPageFields {
    contactUsPageLink = "contactUsPageLink",
}

export interface HeaderPageProps extends PageProps {
    fields: {
        [headerPageFields.contactUsPageLink]: LinkFieldValue;
    };
}
/* Store Custom Code - End */

const mapStateToProps = (state: ApplicationState) => ({
    siteName: state.context.website.name,
    session: getSession(state),
    myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
});

const mapDispatchToProps = {
    dismissRfqUpdates,
};

type Props = HeaderPageProps /* Store Custom Code */ &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    page?: MobiusPageProps;
    container?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    // Starter Custom Code
    wrapper?: InjectableCss;
    contentWrapper?: InjectableCss;
    navWrapper?: InjectableCss;
    bottomContentWrapper?: InjectableCss;
    printWrapper?: InjectableCss;
    // Store Custom Code
    secondaryNavWrapper?: InjectableCss;
    currentAccountNav?: InjectableCss;
}

export const headerStyles: HeaderStyles = {
    /* Store Custom Code - Start */
    // The wrappers have no box so the sticky nav is bounded by the page, not by the header.
    page: {
        css: css`
            display: contents;
        `,
    },
    wrapper: {
        css: css`
            display: contents;
        `,
    },
    /* Store Custom Code - End */
    skipToContent: {
        skipToContent: {
            css: css`
                &:focus {
                    z-index: 6; /* Store Custom Code */
                }
            `,
        },
    },
    contentWrapper: {
        css: css`
            position: relative;
            z-index: 3;
        `,
    },
    navWrapper: {
        css: css`
            /* Store Custom Code - Start */
            position: sticky;
            z-index: 5;
            top: 0;
            /* Store Custom Code - End */
        `,
    },
    bottomContentWrapper: {
        css: css`
            position: relative;
            z-index: 4; /* Store Custom Code */
        `,
    },
    printWrapper: {
        css: css`
            display: none;

            img {
                width: auto;
                height: ${pxToRem(70)};
            }

            @media print {
                display: block;
            }
        `,
    },
    /* Store Custom Code - Start */
    secondaryNavWrapper: {
        css: css`
            position: relative;
            z-index: 4;
        `,
    },
    currentAccountNav: {
        css: css`
            ${headerHideForMobileStyles()}
        `,
    },
    /* Store Custom Code - End */
};

const styles = headerStyles;

const Header = ({ id, siteName, session, toaster, history, myAccountPageUrl, dismissRfqUpdates }: Props) => {
    /* Store Custom Code - Start */
    const navRef = React.useRef<HTMLDivElement>(null);

    usePublishHeightVariable(navRef, stickyHeaderHeightVariable);
    /* Store Custom Code - End */

    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, [myAccountPageUrl]);

    const afterHeader = React.createRef<HTMLSpanElement>();
    return (
        <>
            {/* Starter Removed - siteName */}
            <StyledWrapper {...styles.printWrapper}>
                <Zone contentId={id} zoneName="Logo" />
            </StyledWrapper>
            <Page as="header" isHeader={true} {...styles.page}>
                <SkipNav
                    extendedStyles={styles.skipToContent}
                    text={translate("Skip to main content")}
                    destination={afterHeader}
                />
                <EagerLoadingLogoProvider value={true}>
                    {/* Starter Custom Code - Start */}
                    <StyledWrapper {...styles.wrapper}>
                        {/* Store Removed - contentWrapper */}
                        {/* Store Custom Code - Start */}
                        <StyledWrapper {...styles.secondaryNavWrapper}>
                            <Zone contentId={id} zoneName="SecondaryNav" />
                        </StyledWrapper>
                        {/* Store Custom Code - End */}
                        <StyledWrapper
                            {...styles.navWrapper}
                            data-zone-single
                            // Store Custom Code
                            ref={navRef}
                            data-sticky-header
                        >
                            <Zone contentId={id} zoneName="Nav" />
                            {/* Store Custom Code - Start */}
                            <StyledWrapper {...styles.currentAccountNav} data-zone-single>
                                <Zone contentId={id} zoneName="CurrentAccountNav" />
                            </StyledWrapper>
                            {/* Store Custom Code - End */}
                        </StyledWrapper>
                        <StyledWrapper {...styles.bottomContentWrapper}>
                            <Zone contentId={id} zoneName="ContentBottom" />
                        </StyledWrapper>
                    </StyledWrapper>
                    {/* Starter Custom Code - End */}
                </EagerLoadingLogoProvider>
                <span ref={afterHeader} tabIndex={-1} />
            </Page>
        </>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
        /* Store Custom Code - Start */
        fieldDefinitions: [
            {
                name: headerPageFields.contactUsPageLink,
                displayName: "Contact Us Link",
                editorTemplate: "LinkField",
                fieldType: "General",
                defaultValue: {
                    type: "Page",
                    value: "",
                },
            },
        ],
        /* Store Custom Code - End */
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
